import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  setDoc,
  writeBatch,
  increment,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../config";
import {
  getAuth,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  deleteUser,
} from "firebase/auth";
import { updateListCreatorInfo } from "./readingServices";
import { createNotification } from "./notificationServices";

export async function getUserByUsername(username) {
  const usersRef = collection(db, "Users");
  const q = query(usersRef, where("username", "==", username));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    throw new Error("User not found");
  }

  const userDoc = querySnapshot.docs[0];
  return {
    uid: userDoc.id,
    ...userDoc.data(),
  };
}

export async function updateUserProfile(userId, data) {
  const userRef = doc(db, "Users", userId);

  // Create lowercase versions of searchable fields
  const updateData = {
    ...data,
    updatedAt: new Date().toISOString(),
  };

  if (data.username) {
    updateData.usernameLower = data.username.toLowerCase();
  }

  if (data.displayName) {
    updateData.displayNameLower = data.displayName.toLowerCase();
  }

  await updateDoc(userRef, updateData);

  // If username or displayName was updated, update all lists created by this user
  if (data.username || data.displayName || data.profileImage) {
    const userData = await getUserById(userId);
    await updateListCreatorInfo(userId, userData);
  }
}

export async function createUser(userId, userData) {
  const userRef = doc(db, "Users", userId);

  const userDataWithLower = {
    ...userData,
    usernameLower: userData.username.toLowerCase(),
    displayNameLower: userData.displayName.toLowerCase(),
    followers: 0,
    following: 0,
    pagesRead: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  await setDoc(userRef, userDataWithLower);
  return userDataWithLower;
}

export async function uploadProfileImage(userId, imageBlob) {
  try {
    // First, get the user's current profile image URL
    const userDoc = await getDoc(doc(db, "Users", userId));
    const currentProfileImage = userDoc.data()?.profileImage;

    // If there's an existing profile image, delete it
    if (currentProfileImage) {
      try {
        // Try to extract the path from the URL
        const oldPath = currentProfileImage.split("/o/")[1].split("?")[0];
        const decodedPath = decodeURIComponent(oldPath);
        const oldImageRef = ref(storage, decodedPath);
        await deleteObject(oldImageRef);
      } catch (error) {
        console.error("Error deleting old profile image:", error);
        // Continue with upload even if delete fails
      }
    }

    // Upload new image
    const timestamp = Date.now();
    const storageRef = ref(storage, `profileImages/${userId}_${timestamp}`);
    const uploadResult = await uploadBytes(storageRef, imageBlob);
    const downloadURL = await getDownloadURL(uploadResult.ref);

    // Update user document with new image URL
    await updateDoc(doc(db, "Users", userId), {
      profileImage: downloadURL,
    });

    return downloadURL;
  } catch (error) {
    console.error("Error in uploadProfileImage:", error);
    throw error;
  }
}

export const sendPasswordReset = async (email) => {
  try {
    const auth = getAuth();
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

export async function getUserById(userId) {
  try {
    const userRef = doc(db, "Users", userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error("User not found");
    }

    return {
      uid: userDoc.id,
      ...userDoc.data(),
    };
  } catch (error) {
    console.error("Error fetching user by ID:", error);
    throw error;
  }
}

// Add a function to update existing users with lowercase fields
export async function updateExistingUsersWithLowercase() {
  try {
    const usersRef = collection(db, "Users");
    const querySnapshot = await getDocs(usersRef);

    const updatePromises = querySnapshot.docs.map(async (doc) => {
      const userData = doc.data();
      const updates = {};

      if (userData.username && !userData.usernameLower) {
        updates.usernameLower = userData.username.toLowerCase();
      }

      if (userData.displayName && !userData.displayNameLower) {
        updates.displayNameLower = userData.displayName.toLowerCase();
      }

      if (Object.keys(updates).length > 0) {
        const userRef = doc(db, "Users", doc.id);
        await updateDoc(userRef, updates);
      }
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error updating existing users:", error);
    throw error;
  }
}

export async function updateExistingUsersWithCounts() {
  try {
    const usersRef = collection(db, "Users");
    const querySnapshot = await getDocs(usersRef);
    const batch = writeBatch(db);

    for (const userDoc of querySnapshot.docs) {
      const userId = userDoc.id;
      const userData = userDoc.data();

      // Get follower count
      const followersQuery = query(
        collection(db, "UserFollows"),
        where("followedId", "==", userId)
      );
      const followersSnapshot = await getDocs(followersQuery);

      // Get following count
      const followingQuery = query(
        collection(db, "UserFollows"),
        where("followerId", "==", userId)
      );
      const followingSnapshot = await getDocs(followingQuery);

      // Only update if counts are missing or different
      if (
        userData.followers !== followersSnapshot.size ||
        userData.following !== followingSnapshot.size
      ) {
        const userRef = doc(db, "Users", userId);
        batch.update(userRef, {
          followers: followersSnapshot.size,
          following: followingSnapshot.size,
        });
      }
    }

    await batch.commit();
  } catch (error) {
    console.error("Error updating user follow counts:", error);
    throw error;
  }
}

export async function updateUserPassword(currentPassword, newPassword) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No user is currently signed in");
  }

  try {
    // First, reauthenticate the user
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );
    await reauthenticateWithCredential(user, credential);

    // Then update the password
    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error) {
    console.error("Error updating password:", error);
    if (error.code === "auth/wrong-password") {
      throw new Error("Current password is incorrect");
    }
    throw new Error("Failed to update password: " + error.message);
  }
}

export async function deleteUserAccount(userId) {
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("No user is currently signed in");
  }

  try {
    // Delete user data from Firestore (both Users and UserPrivate)
    const batch = writeBatch(db);
    batch.delete(doc(db, "Users", userId));
    batch.delete(doc(db, "UserPrivate", userId));
    await batch.commit();

    // Delete Firebase Auth account
    await deleteUser(user);

    return { success: true };
  } catch (error) {
    console.error("Error deleting account:", error);
    throw new Error("Failed to delete account: " + error.message);
  }
}

export const followUser = async (followerId, followedId) => {
  try {
    // Get follower's user data for the notification
    const followerDoc = await getDoc(doc(db, "Users", followerId));
    const followerData = followerDoc.data();

    const followRef = doc(db, "UserFollows", `${followerId}_${followedId}`);
    await setDoc(followRef, {
      followerId,
      followedId,
      createdAt: new Date().toISOString(),
    });

    // Create notification for the followed user
    await createNotification({
      userId: followedId,
      type: "NEW_FOLLOWER",
      sourceId: followerId,
      sourceType: "user",
      message: `${followerData.username} started following you`,
      metadata: {
        actorId: followerId,
        actorName: followerData.username,
        actorImage: followerData.profileImage || null,
      },
    });

    // Update follower counts
    await Promise.all([
      updateDoc(doc(db, "Users", followedId), {
        followers: increment(1),
      }),
      updateDoc(doc(db, "Users", followerId), {
        following: increment(1),
      }),
    ]);

    return true;
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

export const updateUserPrivacySettings = async (userId, settings) => {
  try {
    await updateDoc(doc(db, "Users", userId), {
      privacySettings: settings,
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error("Error updating privacy settings:", error);
    throw new Error("Failed to update privacy settings");
  }
};

export const getUserPrivacySettings = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "Users", userId));
    return (
      userDoc.data()?.privacySettings || {
        isProfilePrivate: false,
        hideReadingActivity: false,
        hideBookRatings: false,
      }
    );
  } catch (error) {
    console.error("Error fetching privacy settings:", error);
    throw new Error("Failed to fetch privacy settings");
  }
};

export const isUserFollowing = async (followerId, targetUserId) => {
  try {
    const followDoc = await getDoc(
      doc(db, "UserFollows", `${followerId}_${targetUserId}`)
    );
    return followDoc.exists();
  } catch (error) {
    console.error("Error checking follow status:", error);
    return false;
  }
};
